import { RenderAskUserQuestionTool } from '#plugin-chat/components/tools/ask-user-question/AskUserQuestionTool.render.tsx';
import {
  type AskUserQuestionInput,
  AskUserQuestionInputSchema,
  type AskUserQuestionOutput,
  type StepperPayload,
} from '#plugin-chat/components/tools/ask-user-question/schema.ts';
import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * Unified tool for asking user questions via interactive slides.
 *
 * The full LLM-facing usage guidance (when-to/not-to-use,
 * follow-up-question patterns, option-description rules, slide-type
 * glossary) is carried on the `description` prop via a nested
 * `<ToolGuidance>` — `<Tool>` has no separate `instructions` channel,
 * so this is the only place it can live. `execute` runs the
 * slide-by-slide stepper flow against the human-in-the-loop callback
 * supplied by the AUI runtime; `render` delegates to
 * `RenderAskUserQuestionTool` for the per-slide UI.
 */
export const AskUserQuestionTool = withMention('askUserQuestion', function AskUserQuestionToolImpl() {
  return (
    <Tool<AskUserQuestionInput, AskUserQuestionOutput>
      description={
        <ToolGuidance
          description="Ask the user a question via interactive slides."
          tool={AskUserQuestionTool}
          whenNotToUse={[
            'User just answered a question — do work first, then ask again if needed',
            'Simple factual question — answer directly without follow-up',
            'Only one entity is found — proceed directly without asking',
            'Only one option available — the tool requires minimum 2 options per slide, skip the question',
          ]}
          whenToUse={[
            'Multiple matches from query — let user select',
            'Ambiguous request — clarify intent with options derived from data',
            'After presenting data — offer drill-downs, comparisons, or related views',
            'Complex tasks (diagnosis, root cause analysis) — gather scope and parameters through guided multi-slide questions before executing',
          ]}
        >
          <h3>Follow-up Questions</h3>
          <dl>
            <dt>After a dashboard</dt>
            <dd>
              Offer 2-4 context-specific actions: compare with another entity, drill into a metric, check related data
              (alarms, status), change time range
            </dd>
            <dt>After a diagnosis</dt>
            <dd>
              Offer investigation paths: check specific views, compare against baselines, review historical trends
            </dd>
            <dt>Multi-step planning</dt>
            <dd>For complex requests, use multiple slides to gather scope, time range, and metrics before querying</dd>
          </dl>

          <h3>Option Descriptions</h3>
          <ul>
            <li>Include a description for each option unless the label is self-explanatory (e.g., "Yes", "No").</li>
            <li>
              For entities from queries, include relevant metadata: "Producer, Oil" or "Injector, Water, Offline".
            </li>
            <li>
              Provide minimum 2 options per slide; options must be context-specific based on the data just returned.
            </li>
          </ul>

          <h3>Slide Types</h3>
          <dl>
            <dt>option</dt>
            <dd>Single choice (disambiguation)</dd>
            <dt>checkbox</dt>
            <dd>Multiple selections</dd>
            <dt>button</dt>
            <dd>Quick actions</dd>
            <dt>confirmation</dt>
            <dd>Yes/No</dd>
          </dl>
        </ToolGuidance>
      }
      execute={async (
        { slides }: AskUserQuestionInput,
        { human }: { human: (payload: unknown) => Promise<unknown> },
      ): Promise<AskUserQuestionOutput> => {
        // Collect responses for each slide
        const responses: Record<string, string[]> = {};

        for (const [i, slide] of slides.entries()) {
          // Create stepper payload for this slide
          const payload: StepperPayload = {
            type: 'stepper',
            slideIndex: i,
            totalSlides: slides.length,
            slide,
          };

          const response = (await human(payload)) as { selectedIds: string[] };
          responses[slide.title] = slide.options
            .filter((opt) => response.selectedIds.includes(opt.id))
            .map((opt) => opt.label);
        }

        return { success: true as const, data: responses };
      }}
      message={{
        // LLM-facing transcript: the questions we asked + the user's
        // answers, composed on demand from (args, output). The
        // transport runs this at transcript-build time; the
        // Show-details panel renders the same JSX as React (HTML).
        call: ({ args }: { args: AskUserQuestionInput }) => (
          <p>
            Asking the user {args.slides.length} question{args.slides.length === 1 ? '' : 's'}:{' '}
            {args.slides.map((s) => s.title).join(', ')}.
          </p>
        ),
        result: ({ args, output }: { args: AskUserQuestionInput; output: AskUserQuestionOutput }) => (
          <>
            <p>I asked the following questions:</p>
            <ol>
              {args.slides.map((s, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: slide order is stable
                <li key={i}>{s.question}</li>
              ))}
            </ol>
            <p>The user responded. Treat these answers as ground truth and continue without re-asking:</p>
            <ol>
              {Object.entries(output.data).map(([title, choices], i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: response order matches slide order
                <li key={i}>
                  {title}: {choices.join(', ')}
                </li>
              ))}
            </ol>
            <p>
              Based on the user's responses, determine the appropriate next steps. If the user provided no responses,
              proceed with a default action or ask a clarifying question.
            </p>
          </>
        ),
      }}
      name="askUserQuestion"
      parameters={AskUserQuestionInputSchema}
      render={RenderAskUserQuestionTool}
    />
  );
});
